import Consts from '../Const'
import { DateTimeHelper } from '../DateTimeHelper'
import { Unauthorized } from '../Errors/Unauthorized'
import { GUID, IDataResponse, mapAppTypeToServerAppType } from './ServiceTypes'

export enum EditorNodeType {
  Folder = 0,
  Text = 1,
}

export interface IEditorNode {
  Id: string
  PlayerId: string
  Name: string
  Description: string
  Type: EditorNodeType
  ParentNodeId: string | undefined
  BeforeNodeId: string | undefined
  Deleted: boolean
  Locked: boolean
  CreatedTimestamp: Date | string
  UpdatedTimestamp: Date | string
  DeletedTimestamp: Date | string | undefined
  LastEditTextTimestamp: Date | string
  Tags: string[]
}

export interface IEditorTextInfo {
  Id: GUID
  NodeId: GUID
  IsSnapShot: boolean
  Timestamp: Date | string
}

export interface IEditorAttachment {
  Id: GUID
  NodeId: GUID
  Filename: string
  Size: number
  ContentType: string
  Deleted: boolean
}

interface IAddUpdateEditorNodeRequest {
  EditorNode: IEditorNode
}

interface IRemoveEditorNodeRequest {
  NodeId: GUID
  Force: boolean
}

interface IRemoveEditorAttachmentRequest {
  NodeId: GUID
  EditorAttachmentId: GUID
}

interface IAddEditorTextRequest {
  NodeId: GUID
  Data: string
}

interface ISearchInEditorTextContentByTagsRequest {
  Tags: string[]
  SearchText: string
}

export default class ServiceEditor {
  static mapEditorNodeFromService = (editorNode: IEditorNode) => {
    editorNode.CreatedTimestamp = DateTimeHelper.convertJSONDateTime(editorNode.CreatedTimestamp as any as string)
    editorNode.UpdatedTimestamp = DateTimeHelper.convertJSONDateTime(editorNode.UpdatedTimestamp as any as string)
    editorNode.DeletedTimestamp = DateTimeHelper.convertJSONDateTime(editorNode.DeletedTimestamp as any as string)
    editorNode.LastEditTextTimestamp = DateTimeHelper.convertJSONDateTime(editorNode.LastEditTextTimestamp as any as string)
    return editorNode
  }

  static mapEditorTextInfoFromService = (editorTextInfo: IEditorTextInfo) => {
    editorTextInfo.Timestamp = DateTimeHelper.convertJSONDateTime(editorTextInfo.Timestamp as any as string)
    return editorTextInfo
  }

  static async getAllTags(token: string) {
    const requestUrl = 'editor/getAllTags'

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
    })
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return responseJson as string[]
      })
  }

  static async getEditorNodes(token: string) {
    const requestUrl = 'editor/getEditorNodes'

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
    })
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return (responseJson as IEditorNode[]).map(ServiceEditor.mapEditorNodeFromService)
      })
  }

  static async searchInEditorTextContentByTags(token: string, request: ISearchInEditorTextContentByTagsRequest) {
    const requestUrl = `editor/searchInEditorTextContentByTags`

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
      body: JSON.stringify(request),
    })
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return (responseJson as IEditorNode[]).map(ServiceEditor.mapEditorNodeFromService)
      })
  }

  static async addUpdateEditorNode(token: string, request: IAddUpdateEditorNodeRequest) {
    const requestUrl = 'editor/addUpdateEditorNode'

    console.log('body', request)

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
      body: JSON.stringify(request),
    })
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return responseJson as IDataResponse
      })
  }

  static async removeEditorNode(token: string, request: IRemoveEditorNodeRequest) {
    const requestUrl = 'editor/removeEditorNode'

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
      body: JSON.stringify(request),
    })
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return responseJson as IDataResponse
      })
  }

  static async removeEditorAttachment(token: string, request: IRemoveEditorAttachmentRequest) {
    const requestUrl = 'editor/removeEditorAttachment'

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
      body: JSON.stringify(request),
    })
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return responseJson as IDataResponse
      })
  }

  static async addEditorText(token: string, request: IAddEditorTextRequest) {
    const requestUrl = 'editor/addEditorText'

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
      body: JSON.stringify(request),
    })
      .then(res => {
        if (res.status !== 200) {
          if (res.status === 401) {
            throw new Unauthorized()
          }

          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return responseJson as IDataResponse
      })
  }

  static async getEditorTextContent(token: string, nodeId: GUID, textId?: GUID) {
    const requestUrl = 'editor/getEditorTextContent'

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}?nodeId=${nodeId}&textId=${textId ?? ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
    })
      .then(res => {
        if (res.status !== 200) {
          if (res.status === 401) {
            throw new Unauthorized()
          }

          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return responseJson as IDataResponse
      })
  }

  static async getEditorAttachments(token: string, nodeId: GUID) {
    const requestUrl = 'editor/getEditorAttachments'

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}?nodeId=${nodeId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
    })
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return responseJson as IDataResponse
      })
  }

  static async getEditorTextInfos(token: string, nodeId: GUID, amount: number) {
    const requestUrl = 'editor/getEditorTextInfos'

    return await fetch(`${Consts.defaultBaseUrl}/${requestUrl}?nodeId=${nodeId}&amount=${amount}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        AuthorizationDeviceId: 'Editor',
        AuthorizationToken: token,
        AppType: `${mapAppTypeToServerAppType('editor')}`,
      },
    })
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Error')
        }

        const json = res.json()
        return json
      })
      .then(responseJson => {
        return (responseJson as IEditorTextInfo[]).map(ServiceEditor.mapEditorTextInfoFromService)
      })
  }
}
